class Panel {
  /**
   * Closes the panel and removes listeners.
   */
  closePanel() {
    this.panelToggle.checked = false;
    this.removeGlobalListeners();
    this.panelOpener?.focus();
  }

  /**
   * Toggles panel on Enter|Spacebar key from opener.
   */
  handleOpenerKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.panelToggle.checked = !this.panelToggle.checked;
    this.handleToggleChange();
  }

  /**
   * Closes panel on Escape key.
   */
  handleEscapeKeydown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    this.closePanel();
  }

  /**
   * Closes panel if click is outside.
   */
  handleClickOutside(event) {
    if (this.container.contains(event.target)) {
      return;
    }

    this.closePanel();
  }

  /**
   * Closes panel if focus moves outside.
   */
  handleFocusOut(event) {
    const nextFocused = event.relatedTarget;

    if (!nextFocused) {
      return;
    }

    if (!this.container.contains(nextFocused)) {
      this.closePanel();
    }
  }

  /**
   * Handles panel open/close toggle.
   */
  handleToggleChange() {
    if (this.panelToggle.checked) {
      this.addGlobalListeners();
      this.focusFirstElement();
    } else {
      this.removeGlobalListeners();
    }
  }

  /**
   * Focuses the first focusable element in the panel.
   */
  focusFirstElement() {
    const firstFocusable = this.container.querySelector('[data-first-focusable-element]');
    firstFocusable?.focus();
  }

  /**
   * Adds global event listeners for panel interaction.
   */
  addGlobalListeners() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('keydown', this.handleEscapeKeydown);
    this.container.addEventListener('focusout', this.handleFocusOut);
  }

  /**
   * Removes global event listeners for panel interaction.
   */
  removeGlobalListeners() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('keydown', this.handleEscapeKeydown);
    this.container.removeEventListener('focusout', this.handleFocusOut);
  }

  /**
   * Initializes the Panel with event bindings.
   * @param {Object} configuration - Panel configuration object.
   */
  constructor(configuration) {
    this.container = configuration.container;

    this.panelToggle = this.container.querySelector('[data-panel-toggle]');
    this.panelOpener = this.container.querySelector('[data-panel-opener]');
    this.applyButton = this.container.querySelector('[data-apply]');

    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleFocusOut = this.handleFocusOut.bind(this);
    this.handleEscapeKeydown = this.handleEscapeKeydown.bind(this);
    this.handleOpenerKeydown = this.handleOpenerKeydown.bind(this);
    this.handleToggleChange = this.handleToggleChange.bind(this);

    this.panelOpener?.addEventListener('keydown', this.handleOpenerKeydown);
    this.applyButton?.addEventListener('click', this.closePanel.bind(this));
    this.panelToggle?.addEventListener('change', this.handleToggleChange);
  }
}

export default Panel;
